import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ICellEditorAngularComp} from 'ag-grid-angular';
import {ICellEditorParams} from 'ag-grid-community';
import {GridUtils} from '../../../../shared-utilities/utils-old/grid-utils-old/grid-utils';
import {NumberUtils} from '../../../../shared-utilities/utils-old/shared-utils-old/number-utils';
import {Store} from '@ngrx/store';
import {updateSingleNGPReportVisibleField} from '../../../../features/ngp-report/store/ngp-report.actions';
import {selectSharedGridVisibleFieldsByCurrentPage} from '../../store/shared-grid.selectors';
import {Observable, Subject} from 'rxjs';
import {NGPReport} from '../../../../shared-utilities/models-old/ngp-reports/ngp-report';
import {clearableTextKeys, clearableTextValuesAreEqual, keyToType, StockItem} from '../../../../shared-utilities/models-old/datastructures';
import {selectCurrentNavigationPage} from '../../../../features/user-access/user-stores-store/user-store.selectors';
import {takeUntil} from 'rxjs/operators';
import {IconsEnum} from '../../../shared-icons/models/icons.enum';

@Component({
    selector: 'app-cell-editor-general',
    templateUrl: './cell-editor-general.component.html',
    styleUrls: ['./cell-editor-general.component.scss'],
    standalone: false
})
export class CellEditorGeneralComponent implements ICellEditorAngularComp, OnInit, OnDestroy {

  @ViewChild('input') input: ElementRef;

  currentValue: string | number;
  params: ICellEditorParams;

  colDefField: string;
  colDefFieldType: string;
  currentVisibleFields: Record<string, boolean> = {};


  visibleFields$: Observable<Record<string, boolean>>;
  currentPage$: Observable<string>;
  currentPage: string;
  readonly icons: typeof IconsEnum = IconsEnum;

  private original: number | Date | string | undefined;

  private destroy$ = new Subject<boolean>();

  constructor(private readonly store: Store) {
  }

  //#region GETTERS
  get cellParamDataAtColDefField(): string {
    return String(this.cellParams.value ?? '');
  }

  get cellParams(): ICellEditorParams<NGPReport | StockItem, number> {
    return this.params;
  }

  //#region SETTERS

  set cellParams(params: ICellEditorParams<NGPReport | StockItem>) {
    this.params = params;
  }

  //#endregion

  set columnDefField(field: string) {
    this.colDefField = field;
  }

  set currentNavigationPage(page: string) {
    this.currentPage = page;
  }

  set originalValue(value: string) {
    this.original = value;
  }

  set value(value: string) {
    this.currentValue = value;
  }

  //#endregion

  //#region LIFECYCLE
  agInit(params: ICellEditorParams<NGPReport | StockItem>): void {
    this.cellParams = {...params, data: {...params.data}};
    this.columnDefField = this.cellParams.colDef.field;
    this.value = this.cellParamDataAtColDefField;
    this.originalValue = this.value;

    this.store.select(selectCurrentNavigationPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe((page: string) => {
        this.currentNavigationPage = page;
      });

    this.store.select(selectSharedGridVisibleFieldsByCurrentPage)
      .pipe(takeUntil(this.destroy$))
      .subscribe((visibleFields) => {
        this.currentVisibleFields = visibleFields;
      });
  }

  ngOnInit(): void {
    this.colDefFieldType = typeof this.params.value;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  //#endregion


  selectText(): void {
    setTimeout((): void => {
      this.input.nativeElement.focus();
      this.input.nativeElement.setSelectionRange(0, this.currentValue.toString().length);
      this.input.nativeElement.select();
    }, 1);
  }

  /**
   * Returns true when the editor value differs from the row's original snapshot.
   */
  protected isCurrentValueEdited(): boolean {
    const originalFieldValue: unknown = this.params?.data?.originalValue?.[this.colDefField]?.value;
    if (clearableTextKeys.includes(this.colDefField)) {
      return !clearableTextValuesAreEqual(this.currentValue, originalFieldValue);
    }
    return originalFieldValue !== this.currentValue;
  }

  onUndoChangesClick(): void {
    this.currentValue = this.params.data.originalValue?.[this.colDefField]?.value ?? '';
    this.params.data[this.colDefField] = this.currentValue;
    GridUtils.removeIsEditingFlagAndDispatch(this.params.data as NGPReport | StockItem, this.colDefField, this.store, this.currentPage);
    GridUtils.updateIsEditedAndForceRefresh(this.params);
  }

  getValue(): string | number {
    this.params.value = this.currentValue;
    const type = this.params.colDef.cellDataType;
    GridUtils.updateIsEditedAndForceRefresh(this.params);

    const originalFieldValue: unknown = this.params.data.originalValue?.[this.colDefField]?.value;
    const isTextField: boolean = typeof originalFieldValue === 'string'
      || (originalFieldValue === undefined && keyToType[this.colDefField] === 'string');

    let newValue: string | number;
    if (isTextField) {
      newValue = this.currentValue.toString();
      if (this.params.data[this.colDefField] === '' && !clearableTextKeys.includes(this.colDefField)) {
        newValue = (originalFieldValue ?? '') as string;
      }
      this.params.data[this.colDefField] = newValue;
      GridUtils.updateSingleItem(this.params.data as NGPReport | StockItem, this.colDefField, newValue, this.store, this.currentPage);
    } else if (typeof originalFieldValue === 'number') {
      newValue = +NumberUtils.formatNumberToDecimals(+this.currentValue, 2);
      if (this.colDefField === 'sellPriIncl1') {
        this.changeNgpOnPriceChange(newValue);
      }
      newValue = this.params.data[this.colDefField] === 0 && this.colDefField !== 'barcode' ? this.params.data.originalValue[this.colDefField].value : newValue;
      this.params.data[this.colDefField] = newValue;
      GridUtils.updateSingleItem(this.params.data as NGPReport | StockItem, this.colDefField, newValue, this.store, this.currentPage);
    }
    return newValue;
  }


  sanitizeInput(value: string): string {
    const sanitizedValue = value.replace(/[^0-9.]/g, '');

    // Allow only the first occurrence of a decimal point
    const parts = sanitizedValue.split('.');
    return parts.length > 2 ? `${parts[0]}.${parts[1]}` : sanitizedValue;
  }

  changeNgpOnPriceChange(newValue: number): void {
    if (!this.currentVisibleFields.nominalGP) {
      this.store.dispatch(updateSingleNGPReportVisibleField({colId: 'nominalGP', value: true}));
    }
    this.params.data.nominalGP = newValue;

  }

  onKeyUp(event: KeyboardEvent): void {
    let sanitizedValue: string | number;
    const sanitizeFields = ['recommendedGP', 'ordLvl', 'packSize', 'sellPriExcl1'];
    if (sanitizeFields.includes(this.colDefField)) {
      sanitizedValue = this.sanitizeInput(this.currentValue as string);
    } else {
      sanitizedValue = this.currentValue;
    }

    this.params.value = sanitizedValue;
    this.params.data[this.colDefField] = sanitizedValue;
    this.currentValue = sanitizedValue;
    GridUtils.updateIsEditedAndForceRefresh(this.params);
  }

  afterGuiAttached(): void {
    this.input.nativeElement.focus();
  }

}
